import { LandscapeLock } from 'components/containers';
import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useParams } from 'react-router-dom';
import { useTheme, useFullScreen, useCurrentUser } from 'hooks';
import { getPhotoSeries } from 'redux/root.selectors';
import { useSelector } from 'react-redux';

import FeedbackComment from './containers/comment.component';
import FeedbackRating from './containers/rating.component';
import FeedbackThankYou from './containers/thank-you.component';
import { makePost } from 'services';
import { PhotoSeries } from 'redux/photoseries/photoseries';
import WindshieldRepairDecision from './containers/windshield-repair-decision.component';

const FeedbackWrapper = styled.div<{ background: string }>`
  text-align: center;
  background: ${({ background }) => background};
  min-height: var(--view-height);
`;

enum Step {
  Rating,
  Comment,
  ThankYou,
  WindshieldRepairDecision
}

interface Params {
  photoSeriesId: string;
}

const Feedback = () => {
  const { toggleFullScreen } = useFullScreen();
  const { photoSeriesId } = useParams<Params>();

  const photoSeries: PhotoSeries = useSelector(getPhotoSeries);
  const currentUser = useCurrentUser()!;
  const { styles } = useTheme();

  const getStep = (initial: boolean) => {
    if (initial
        && photoSeries.windshieldRepairReplaceDecision !== undefined
        && photoSeries.smartScanWindshieldRepairSummaryEnabled) {
      return Step.WindshieldRepairDecision;
    }

    if (photoSeries.userFeedbackSettings.satisfactionRatingEnabled) {
      return Step.Rating;
    }

    if (photoSeries.userFeedbackSettings.commentEnabled) {
      return Step.Comment;
    }

    return Step.ThankYou;
  };

  const [satisfactionRating, setSatisfactionRating] = useState<number>(-1);
  const [comment, setComment] = useState<string>('');
  const [step, setStep] = useState<Step>(getStep(true));
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  // Automatically go to the next step after 1.2 seconds if the user has
  // given postitive rating
  useEffect(() => {
    if (satisfactionRating === -1) return;
    let timer = setTimeout(() => {
      setStep(photoSeries.userFeedbackSettings.commentEnabled ? Step.Comment : Step.ThankYou);
    }, 1200);
    return () => clearTimeout(timer);
  }, [satisfactionRating, photoSeries.userFeedbackSettings.commentEnabled]);

  const handleSubmitFeedback = () => {
    setIsSubmitting(true);
    const data = {
      photoSeriesId,
      satisfactionRating: satisfactionRating === -1
        ? null
        : satisfactionRating,
      comment
    };
    makePost(`capture/${photoSeriesId}/feedback`, data)
      .subscribe(() => {
        setIsSubmitting(false);
        toggleFullScreen(true);
        setStep(Step.ThankYou);
      });
  };

  return (
    <FeedbackWrapper background={styles.colorBackgroundGradient} className="wrapper">
      {
        (() => {
          switch (step) {
            case Step.WindshieldRepairDecision:
              return (
                <WindshieldRepairDecision
                  repairDecision={photoSeries.windshieldRepairReplaceDecision!}
                  photoSeriesId={photoSeriesId}
                  vin={photoSeries.vehicle.vin}
                  onContinueClicked={() => setStep(getStep(false))}
                  customerName={currentUser.customerName}
                  showDownloadImagesButton={photoSeries.smartScanImageDownloadEnabled}
                />
              );
            case Step.Rating:
              return (
                <LandscapeLock>
                  <FeedbackRating
                    rating={satisfactionRating}
                    setRating={setSatisfactionRating}
                  />
                </LandscapeLock>
              );
            case Step.Comment:
              return (
                <FeedbackComment
                  comment={comment}
                  setComment={setComment}
                  submit={handleSubmitFeedback}
                  disabled={isSubmitting}
                />
              );
            case Step.ThankYou:
              return (
                <FeedbackThankYou />
              );
            default:
              return null;
          }
        })()
      }
    </FeedbackWrapper>
  );
};

export default Feedback;
